import type {JSONNode} from '@packages/shared/types/JSONNode';
import type {Plugins} from '@packages/systems/wfdl';
import {
  isSymbolRoot,
  isSymbolInst,
} from '@packages/systems/core/utils/DomTypeUtils';
import {reportBug} from '@packages/utilities/reporter';
import {
  type UserComponentUsage,
  type UserComponentUsageAncestorFlags,
} from './types';
import {isAncestorToBeFlagged} from './ancestorFlagUtils';
import {getSlotOverrideIdsFromInstance} from '@packages/shared/js';

/**
 * Utility that reads a JSON DOM node tree and returns an array of User
 * Component usage metadata.
 */
export function jsonNodesToUserComponentUsages(
  dataTypes: Plugins.DataTypes,
  jsonNodes: JSONNode[]
): UserComponentUsage[] {
  // parentIndex is a map of node ID to the node's parent. We use it to walk
  // up the tree and check if the ancestor tree has any nodes to flag.
  const parentIndex: {
    [jsonNodeId: string]: JSONNode;
  } = {};

  // instanceRootMap is a map of node ID to User Component Root ID. We use it
  // to store all the User Component instances that we encounter while walking
  // the tree. We include the associated User Component Root IDs in the usages.
  const instanceRootMap: {
    [jsonNodeId: string]: string;
  } = {};

  const jsonNodesLength = jsonNodes.length;

  for (
    let jsonNodeIndex = 0;
    jsonNodeIndex < jsonNodesLength;
    jsonNodeIndex++
  ) {
    const jsonNode = jsonNodes[jsonNodeIndex];

    if (!jsonNode) {
      continue;
    }

    const children = jsonNode.children || [];
    const childrenLength = children.length;

    for (let childIndex = 0; childIndex < childrenLength; childIndex++) {
      const childId = children[childIndex];

      if (typeof childId === 'string') {
        parentIndex[childId] = jsonNode;
      }
    }

    if (jsonNode.data && isSymbolInst(jsonNode.data)) {
      instanceRootMap[jsonNode._id] = jsonNode.data.sym.inst;

      const slotOverrideKeys = getSlotOverrideIdsFromInstance(
        dataTypes,
        jsonNode
      );

      for (const slotOverrideKey of slotOverrideKeys) {
        const slotChildren: unknown =
          jsonNode.data.sym.overrides?.[slotOverrideKey];

        if (!Array.isArray(slotChildren)) {
          // If slotChildren is not an array, the data is corrupt,
          // and we must ignore it, and continue to the next property type.
          continue;
        }

        const slotChildrenLength = slotChildren.length;
        for (
          let slotChildIndex = 0;
          slotChildIndex < slotChildrenLength;
          slotChildIndex++
        ) {
          const slotChild: unknown = slotChildren[slotChildIndex];
          // We check that slotChild is a string because we may have
          // Slot overrides that are not correctly flattened. When the
          // slotChild is not a string we can't do much other than ignore it.
          if (typeof slotChild === 'string') {
            parentIndex[slotChild] = jsonNode;
          }
        }
      }
    }
  }

  const usages: Array<UserComponentUsage> = [];

  for (const instanceId in instanceRootMap) {
    const rootId = instanceRootMap[instanceId];
    let parentRootId = null;
    const ancestorFlags: UserComponentUsageAncestorFlags = {};
    let hasAncestorFlags = false;

    // As we walk up the ancestor tree, we track node IDs in seenParents
    // to detect cyclical trees that would put us in an infinite loop.
    const seenParents: {
      [jsonNodeId: string]: true;
    } = {};

    let parent = parentIndex[instanceId];
    while (parent) {
      if (seenParents[parent._id]) {
        // If we are here we have a corrupt cyclical element tree and there
        // isn't much we can do but to break out of the infinite loop.
        reportBug('CyclicalElementTree', 'Corrupt cyclical element tree', {
          corruptNode: parent._id,
          team: 'Platform',
          ticket: 'https://webflow.atlassian.net/browse/PLAT-1773',
        });
        break;
      }
      seenParents[parent._id] = true;
      const parentType = parent.type;
      if (parentType && isAncestorToBeFlagged(parentType)) {
        ancestorFlags[parentType] = true;
        hasAncestorFlags = true;
      }

      const nextParent = parentIndex[parent._id];

      if (!nextParent && isSymbolRoot(parent.data)) {
        // If the root of the ancestor tree is a User Component Root, we
        // know the User Component instance we encountered is nested inside
        // another User Component. We set the parentRootId for nested
        // User Component instances.
        parentRootId = parent._id;
      }

      parent = nextParent;
    }

    // Sitewide User Component validation currently only cares about User
    // Component instances that are inside another User Components or elements
    // where Collection Lists are not allowed.
    if (hasAncestorFlags || parentRootId) {
      usages.push({
        instanceId,
        // @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
        rootId,
        parentRootId,
        ancestorFlags,
      });
    }
  }

  return usages;
}

export const USER_COMPONENT_USAGE_SCHEMA_VERSION = 1;
